/**
 * RFC 7807 `application/problem+json` errors, as the backend actually emits them.
 *
 * Source of truth: `backend/kernel/api/exceptions.py`. The body is:
 *
 *   {
 *     "type": "about:blank#<code>",
 *     "title": "Not found",
 *     "status": 404,
 *     "code": "not_found",
 *     "detail": "Not found.",
 *     "errors": { "field": ["message"] },   // validation only
 *     "correlation_id": "..."               // when the request carried one
 *   }
 *
 * ---------------------------------------------------------------------------
 * THE FOUR STATUSES, AND WHY EACH IS DIFFERENT
 * ---------------------------------------------------------------------------
 *
 * 401 — re-authenticate. Also returned when a portal session reaches a staff
 *       route: that is "the wrong door", not "insufficient rights".
 *
 * 403 — NOT PERMITTED. The user can see the record but may not do this to it.
 *       Show it. `detail` is safe to display.
 *
 * 404 — GENUINELY ABSENT, as far as this user is concerned, AND NOTHING MORE.
 *       The backend deliberately returns 404 both for records that do not exist
 *       and for records the caller may not see (CROSS_TENANT, OUT_OF_SCOPE_READ),
 *       precisely so the two are indistinguishable. A 403 there would confirm
 *       that a record exists in another firm, which is itself a disclosure.
 *
 *       => NEVER render "you do not have permission" for a 404. Not "you may not
 *          have access", not "this may be restricted". Render "not found". Any
 *          hedged wording re-opens the disclosure the backend closed.
 *
 * 409 — A STATE GUARD REFUSED, and `detail` CARRIES A REASON THAT IS MEANT TO BE
 *       SHOWN TO THE USER VERBATIM. e.g. "No other qualified approver is
 *       available", "Outstanding mandatory documents". It is actionable and
 *       discloses nothing the user could not already see. Do not replace it with
 *       a generic message.
 */

export interface ProblemDetail {
  /** `about:blank#<code>` */
  type: string;
  title: string;
  status: number;
  /** Stable machine-readable classification, e.g. `not_permitted`, `state_guard`. */
  code: string;
  /** Human-readable. Safe to show for 403 and 409; see the rules above for 404. */
  detail: string;
  /** Field-level validation errors. Present on 400 only. */
  errors?: Record<string, string[] | string>;
  correlation_id?: string;
}

/** Base class for every failure the client produces. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly detail: string;
  readonly problem: ProblemDetail | null;
  readonly correlationId: string | null;

  constructor(
    message: string,
    opts: {
      status: number;
      code: string;
      title?: string;
      detail?: string;
      problem?: ProblemDetail | null;
      correlationId?: string | null;
    },
  ) {
    super(message);
    this.name = new.target.name;
    this.status = opts.status;
    this.code = opts.code;
    this.title = opts.title ?? message;
    this.detail = opts.detail ?? message;
    this.problem = opts.problem ?? null;
    this.correlationId = opts.correlationId ?? null;
  }
}

/**
 * 401. The session is absent, expired, revoked, or belongs to another audience.
 * The app should send the user back to login (see `ApiClient` `onUnauthenticated`).
 */
export class UnauthenticatedError extends ApiError {}

/**
 * 403. The actor can see the record but is not permitted this action.
 * This one you DO show as a permission problem — it is the only status where
 * "you do not have permission" is a truthful and non-disclosing thing to say.
 */
export class ForbiddenError extends ApiError {}

/**
 * 404. Absent as far as this caller is concerned.
 *
 * Read the module header before writing any UI for this. The backend collapses
 * "does not exist", "belongs to another firm" and "outside your scope" into this
 * one answer on purpose. Rendering anything permission-flavoured here undoes it.
 */
export class NotFoundError extends ApiError {}

/**
 * 409. A workflow guard, work gate, two-person control, legal hold or a stale
 * `If-Match` refused the write.
 *
 * `detail` is written for the user and MUST be shown verbatim.
 */
export class ConflictError extends ApiError {
  /** True when this is an optimistic-concurrency failure rather than a domain guard. */
  get isStaleWrite(): boolean {
    return this.code === 'stale_write' || this.code === 'precondition_failed';
  }
}

/** 400 with field-level `errors`. Feed `fieldErrors` into react-hook-form. */
export class ValidationError extends ApiError {
  readonly fieldErrors: Record<string, string>;

  constructor(
    message: string,
    opts: ConstructorParameters<typeof ApiError>[1] & {
      fieldErrors?: Record<string, string>;
    },
  ) {
    super(message, opts);
    this.fieldErrors = opts.fieldErrors ?? {};
  }
}

/** 429. Respect `Retry-After` when the header is present. */
export class RateLimitError extends ApiError {
  readonly retryAfterSeconds: number | null;

  constructor(
    message: string,
    opts: ConstructorParameters<typeof ApiError>[1] & { retryAfterSeconds?: number | null },
  ) {
    super(message, opts);
    this.retryAfterSeconds = opts.retryAfterSeconds ?? null;
  }
}

/** 5xx. */
export class ServerError extends ApiError {}

/** The request never produced an HTTP response: offline, DNS, CORS, abort. */
export class NetworkError extends ApiError {
  /** `override` because `Error` already declares `cause`. */
  override readonly cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message, { status: 0, code: 'network_error', title: 'Network error', detail: message });
    this.cause = cause;
  }
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/**
 * Field errors, keyed by field, ready for react-hook-form.
 *
 * A nested serializer answers with a nested object -- `{"profile": {"date_of_birth":
 * "..."}}`, `{"custom_fields": {"visa_category": "..."}}` -- and those used to fall
 * through both branches below and vanish. The screen showed only the generic detail
 * while the server had said exactly which field was wrong and why, which reads as the
 * form rejecting something for no reason.
 *
 * One level of nesting, keyed `parent.child`, which matches how react-hook-form names
 * a nested field. Deeper than that is `contacts[0].email` territory -- a real shape but
 * a different problem, and guessing at an index here would put a message under the
 * wrong contact.
 */
function flattenFieldErrors(errors: ProblemDetail['errors']): Record<string, string> {
  const out: Record<string, string> = {};
  if (!errors) return out;

  const first = (value: unknown): string | undefined => {
    if (typeof value === 'string') return value;
    // DRF wraps most messages in a list.
    if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    return undefined;
  };

  for (const [field, value] of Object.entries(errors)) {
    const direct = first(value);
    if (direct !== undefined) {
      out[field] = direct;
      continue;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      for (const [child, nested] of Object.entries(value as Record<string, unknown>)) {
        const message = first(nested);
        if (message !== undefined) out[`${field}.${child}`] = message;
      }
    }
  }
  return out;
}

function isProblem(value: unknown): value is ProblemDetail {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ProblemDetail).status === 'number'
  );
}

const DEFAULT_TITLES: Record<number, string> = {
  400: 'Bad request',
  401: 'Unauthenticated',
  403: 'Forbidden',
  404: 'Not found',
  409: 'Conflict',
  429: 'Too many requests',
};

/**
 * Turn a non-OK response body into the right `ApiError` subclass.
 *
 * Tolerant by design: a proxy, a 502 from a load balancer, or a Django debug
 * page will not be problem+json, and the client still has to produce something
 * with the correct status semantics.
 */
export function toApiError(
  status: number,
  body: unknown,
  headers?: Headers,
  fallbackCorrelationId?: string | null,
): ApiError {
  const problem = isProblem(body) ? body : null;
  const code = problem?.code ?? `http_${status}`;
  const title = problem?.title ?? DEFAULT_TITLES[status] ?? 'Error';
  const detail = problem?.detail ?? title;
  const correlationId =
    problem?.correlation_id ?? headers?.get('X-Correlation-Id') ?? fallbackCorrelationId ?? null;

  const base = { status, code, title, detail, problem, correlationId };

  if (status === 401) return new UnauthenticatedError(detail, base);
  if (status === 403) return new ForbiddenError(detail, base);
  if (status === 404) return new NotFoundError(detail, base);
  if (status === 409) return new ConflictError(detail, base);
  if (status === 429) {
    const header = headers?.get('Retry-After');
    const retryAfterSeconds = header ? Number.parseInt(header, 10) : null;
    return new RateLimitError(detail, {
      ...base,
      retryAfterSeconds: Number.isFinite(retryAfterSeconds) ? retryAfterSeconds : null,
    });
  }
  if (status >= 500) return new ServerError(detail, base);
  if (status === 400 || status === 422) {
    return new ValidationError(detail, {
      ...base,
      fieldErrors: flattenFieldErrors(problem?.errors),
    });
  }
  return new ApiError(detail, base);
}

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
export const isUnauthenticated = (e: unknown): e is UnauthenticatedError =>
  e instanceof UnauthenticatedError;
export const isForbidden = (e: unknown): e is ForbiddenError => e instanceof ForbiddenError;
export const isNotFound = (e: unknown): e is NotFoundError => e instanceof NotFoundError;
export const isConflict = (e: unknown): e is ConflictError => e instanceof ConflictError;
export const isValidationError = (e: unknown): e is ValidationError => e instanceof ValidationError;

/**
 * The one function every screen should use to turn an error into words.
 *
 * It exists so the 404 rule is applied in exactly one place. If you find
 * yourself writing `if (err.status === 403) ...` in a component, use this
 * instead — the interesting cases are already decided here.
 */
export function userMessage(error: unknown): string {
  if (error instanceof ConflictError) {
    // 409 detail is authored for the user. Show it exactly as given.
    return error.detail;
  }
  if (error instanceof ForbiddenError) {
    // The only status where a permission message is correct.
    return error.detail || 'You do not have permission to perform this action.';
  }
  if (error instanceof NotFoundError) {
    // NOT a permission message. See the module header. The backend answers 404
    // for records in another firm and for records outside the caller's scope
    // precisely so this message cannot distinguish them.
    return 'Not found.';
  }
  if (error instanceof UnauthenticatedError) {
    return 'Your session has ended. Please sign in again.';
  }
  if (error instanceof RateLimitError) {
    return error.retryAfterSeconds
      ? `Too many requests. Try again in ${error.retryAfterSeconds} seconds.`
      : 'Too many requests. Please wait a moment and try again.';
  }
  if (error instanceof ValidationError) {
    return error.detail || 'Please check the highlighted fields.';
  }
  if (error instanceof NetworkError) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  if (error instanceof ServerError) {
    return 'Something went wrong on our side. The problem has been logged.';
  }
  if (error instanceof ApiError) return error.detail;
  return 'Something went wrong.';
}

/**
 * Field errors ready for `react-hook-form`'s `setError`.
 * Returns `{}` for anything that is not a field-level validation failure.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  return error instanceof ValidationError ? error.fieldErrors : {};
}
